import * as PIXI from "pixi.js";
import i18next from "i18next";
import { Spine } from "@esotericsoftware/spine-pixi-v7";
import GameViewSimpleController from "../GameViewSimple/GameViewSimpleController";
import { FONT_STYLES, scaleStyle } from "../../../fontStyles";
import { Settings } from "../../../settings";
import { ULTRA_WIDE } from "../../../Contexts/DimensionsContext";
import { ZONE_THRESHOLDS } from "../../../RGS/Providers/PopiPlay/holdAndWinState";

/**
 * HoldAndWinGameViewController — the Cleopatra's Fire Hold & Win room.
 *
 * hold_and_win_game_view shares the base rig's plumbing (reels bone, layout
 * skins, `reel_landscape` constraint, jackpot column) but not its layout: a
 * tall 5x8 frame (`nhw_reel_p`), and the respin counter and the collector are
 * BUILT INTO this spine (counter_* / collector_* clips, `txt_slot_collector`).
 * So, like FreeSpinsGameViewController, this only points GameViewSimple-
 * Controller at the other spine and at its own registry keys.
 *
 * Its idle is `hnw_bg_idle`. The logo slot `logo_placeholder` is a hand edit
 * too (landscape: above the royal jackpot, the base room's offset; portrait:
 * centred above the single jackpot row — a first guess), so attachLogo()
 * injects the logo exactly as in the other two rooms.
 *
 * The reel slots and the window clip are HAND EDITS in
 * hold_and_win_game_view.json (a re-export drops them): bone
 * `attachment_slot_placeholder` (-0.5, 9) under `reels` — the centre of the
 * 5x8 cell grid drawn into the frame — and clip `reels_window_clip`
 * (x -423.5..422.5, y 562..-544). The grid's cells are 170 x 139 spine units.
 */
export const HOLD_AND_WIN_GAME_VIEW_SLOTS = {
  REEL_SET: "holdAndWinGameView.reelSet",
  OVERLAY_REEL_SET: "holdAndWinGameView.overlayReelSet",
  // `respin_counter_placeholder` (hand-added, under the bottom row): the
  // standalone hold_and_win_counter spine — the respin counter now, the
  // End Collector later (the counter turns into it).
  RESPIN_COUNTER: "holdAndWinGameView.respinCounter",
};

// The 5x8 cell grid drawn into the frame, in spine units (nhw_reel_p).
export const HOLD_AND_WIN_CELL = { WIDTH: 170, HEIGHT: 139 };

// The coin counter on the Royal plate: filled cells / 40 (the Royal is all 40
// filled, GDD §8.9). Pot Collection's X/15 counter (GameViewController
// .updateCoinCounter), on this room's `txt_slot_royal_counter` slot. Spine
// units — the slot scales it with the plate.
export const HOLD_AND_WIN_COIN_COUNTER = {
  SLOT: "txt_slot_royal_counter",
  TOTAL: 40,
  FONT_SIZE: 30,
  MAX_WIDTH: 90, // the dark box on the plate; wider text is squeezed to fit
};
// The locked rows (animator, 2026-09-30: `locked_reel` — GDD "Locked-Row
// Symbols": "Show dormant symbols through a locked overlay until
// activation"). One plate per locked row, in the room's
// attachment_locked_reel_1..4 slots: _1 is data row 4 (the row directly above
// the active zone), _4 the top row. The slots sit last in the draw order, so
// a plate covers its row's cells and spines; its dark body is see-through,
// so the dormant symbols show under it. `idle` loops while the row is
// locked, `out` (0.5 s) plays as it opens. Replaces the black bars
// HoldAndWinOverlay drew with anime.js.
export const HOLD_AND_WIN_LOCKED_ROWS = {
  SLOT_PREFIX: "attachment_locked_reel_",
  FIRST_LOCKED_ROW: 4,
  COUNT: 4,
  SPINE: { SKELETON: "locked_reel-skeleton", ATLAS: "locked_reel-atlas" },
  IDLE: "idle",
  OUT: "out",
  // The plate's text: the active-zone count that opens the row (10 / 15 /
  // 20 / 25, ZONE_THRESHOLDS), left of the coin / scarab / coin icons.
  TEXT_SLOT: "txt_slot_locked_reel",
  // FONT_SIZE: 64,
  // MAX_WIDTH: 300,
  // "N MORE" (design, 2026-09-30): how many more active-zone symbols open
  // THIS row — its count minus cells_in_zone, updated as each one lands. The
  // Royal plate's X/40 counts the whole grid; this counts for its own row.
  // Spine units. The pair is right-aligned at RIGHT_EDGE (from the text
  // slot's centre), so "3 MORE" and "18 MORE" end at the same place.
  COUNT_FONT_SIZE: 68,
  MORE_FONT_SIZE: 48,
  GAP: 20,
  RIGHT_EDGE: 101,
  MORE_KEY: "hold_and_win_locked_row_more",
};

// The reference the padding is measured around: the BASE board (5x4 x 214),
// exactly as PixiSettings.REELSET_LAYOUT_CONFIGS measures it. So the same
// numbers give the same room as the base scale did, and each layout's
// numbers are a pure tuning knob from there (Vladimir, 2026-09-28).
const REFERENCE_WIDTH = Settings.REEL_COUNT * Settings.SYMBOL_WIDTH;
const REFERENCE_HEIGHT = Settings.SYMBOLS_VISIBLE * Settings.SYMBOL_HEIGHT;

// ============================================================================
// THIS ROOM'S OWN PADDING — per layout
//
// The base and free-spins rooms are drawn at the BASE board's scale (the
// 5x4 grid fitted by PixiSettings.REELSET_LAYOUT_CONFIGS). This room gets its
// own table so it can be tuned apart from them. Same formula, same reference
// board (see REFERENCE_WIDTH), capped at 1.0 like the base game (2.0 past
// ULTRA_WIDE) — so identical numbers draw it exactly as the base scale did.
//
// Same units as REELSET_LAYOUT_CONFIGS: multiples of Settings.SYMBOL_WIDTH
// (sides, both sides together) and SYMBOL_HEIGHT (top, bottom) — 214 — so the
// numbers read the same as the table in pixiSettings. Smaller padding =
// bigger room.
//
// Starting values: a straight copy of PixiSettings.REELSET_LAYOUT_CONFIGS
// (2026-09-28). Tune per layout from here.
// ============================================================================
export const HOLD_AND_WIN_LAYOUT_CONFIGS = {
  MOBILE_SMALL_PORTRAIT: { sides: 0.35, top: 4.125, bottom: 4.125 },
  MOBILE_SMALL_LANDSCAPE: { sides: 5.0, top: 0.7, bottom: 0.7 },
  MOBILE_PORTRAIT: { sides: 0.35, top: 2.5, bottom: 2.5 },
  MOBILE_LANDSCAPE: { sides: 5.25, top: 0.7, bottom: 0.7 },
  TABLET_PORTRAIT: { sides: 0.75, top: 3.5, bottom: 3.5 },
  TABLET_LANDSCAPE: { sides: 5.25, top: 0.8, bottom: 0.8 },
  SMALL_DESKTOP: { sides: 5.25, top: 0.5, bottom: 0.5 },
  DESKTOP: { sides: 5.25, top: 0.5, bottom: 0.5 },
  WIDE_DESKTOP: { sides: 5.25, top: 0.5, bottom: 0.5 },
  ULTRA_WIDE_DESKTOP: { sides: 5.25, top: 0.5, bottom: 0.5 },
};

// Moves the whole room, in multiples of SYMBOL_HEIGHT at the room's scale.
// y: POSITIVE = UP, x: POSITIVE = RIGHT — the same signs as
// GAME_VIEW_OFFSET_CONFIG.
export const HOLD_AND_WIN_OFFSET_CONFIG = {
  MOBILE_SMALL_PORTRAIT: { x: 0, y: 0 },
  MOBILE_SMALL_LANDSCAPE: { x: 0, y: 0 },
  MOBILE_PORTRAIT: { x: 0, y: -0.0 },
  MOBILE_LANDSCAPE: { x: 0, y: 0 },
  TABLET_PORTRAIT: { x: 0, y: 0 },
  TABLET_LANDSCAPE: { x: 0, y: 0 },
  SMALL_DESKTOP: { x: 0, y: 0 },
  DESKTOP: { x: 0, y: 0.1 },
  WIDE_DESKTOP: { x: 0, y: 0.1 },
  ULTRA_WIDE_DESKTOP: { x: 0, y: 0 },
};

// ============================================================================
// THE REEL BOX — per layout, apart from the room (2026-09-29)
//
// The tables above size and place the WHOLE room (background included). This
// one scales only the reel box inside it — the frame, the 5x8 cells, the
// respin counter and the collector, which all hang off the `reels` bone —
// the way Mad Labs scaled its reels apart from its background.
//
// 1.0 = as the spine is authored (what is on screen today). 0.9 = the box 10%
// smaller, the background untouched. It scales about the box's own centre.
// Multiplies the landscape skin's own 0.8485 (the `reel_landscape`
// constraint), so 1.0 keeps landscape exactly as it is now.
// ============================================================================
export const HOLD_AND_WIN_REEL_SCALE_CONFIG = {
  MOBILE_SMALL_PORTRAIT: 1.1,
  MOBILE_SMALL_LANDSCAPE: 1.0,
  MOBILE_PORTRAIT: 1.0,
  MOBILE_LANDSCAPE: 1.0,
  TABLET_PORTRAIT: 1.0,
  TABLET_LANDSCAPE: 1.0,
  SMALL_DESKTOP: 1.0,
  DESKTOP: 1.0,
  WIDE_DESKTOP: 1.1,
  ULTRA_WIDE_DESKTOP: 1.0,
};

// ============================================================================
// PORTRAIT: reel box + jackpots + logo AS ONE (2026-09-29)
//
// In landscape the three sit apart (the box on the right, the jackpot column
// and the logo on the left) and HOLD_AND_WIN_REEL_SCALE_CONFIG sizes the box
// alone. In portrait they are one stack — logo, jackpot row, box — so they
// scale and move TOGETHER, the background staying where it is.
//
//   scale  1.0 = as authored. About the scene's centre, so the stack keeps
//          its own proportions and spacing.
//   x, y   in multiples of SYMBOL_HEIGHT at the room's scale, the same unit
//          and signs as HOLD_AND_WIN_OFFSET_CONFIG: y POSITIVE = UP,
//          x POSITIVE = RIGHT.
//
// Portrait layouts only; a layout with no entry is left as authored.
// HOLD_AND_WIN_REEL_SCALE_CONFIG still multiplies the box on top of this.
// ============================================================================
export const HOLD_AND_WIN_PORTRAIT_GROUP_CONFIG = {
  MOBILE_SMALL_PORTRAIT: { scale: 1.02, x: 0, y: -0.45 },
  MOBILE_PORTRAIT: { scale: 1.0, x: 0, y: -0.85 },
  TABLET_PORTRAIT: { scale: 1.0, x: 0, y: 0 },
};

// The group's bones — all children of the root, so one transform about the
// root moves them as a rigid whole:
//   reels                     the box (frame, cells, counter, collector)
//   _anchor                   the portrait jackpot plates' constraint targets
//                             (land_jckp_*2, major_portrait), so the plates
//                             follow it
//   attachment_logo_portrait  the logo's portrait slot
const PORTRAIT_GROUP_BONES = ["reels", "_anchor", "attachment_logo_portrait"];

/**
 * The scale this room's spine is drawn at, from its own padding table. Used
 * by the controller for the spine and by HoldAndWinGameView for the counter-
 * scale of the injected board, so the two always agree.
 */
export const holdAndWinSceneScale = (dimensions, layout) => {
  if (!dimensions?.width || !dimensions?.height) return 1;
  const pad = HOLD_AND_WIN_LAYOUT_CONFIGS[layout] ?? {
    sides: 0,
    top: 0,
    bottom: 0,
  };
  const neededWidth = REFERENCE_WIDTH + pad.sides * Settings.SYMBOL_WIDTH;
  const neededHeight =
    REFERENCE_HEIGHT + (pad.top + pad.bottom) * Settings.SYMBOL_HEIGHT;
  const fit = Math.min(
    dimensions.width / neededWidth,
    dimensions.height / neededHeight,
  );
  const cap =
    dimensions.width > ULTRA_WIDE.BREAKPOINT ? ULTRA_WIDE.MAX_SCALE : 1;
  return Math.min(cap, fit);
};

class HoldAndWinGameViewController extends GameViewSimpleController {
  static SCENE = {
    TAG: "HoldAndWinGameView",
    SKELETON: "hold_and_win_game_view-skeleton",
    ATLAS: "hold_and_win_game_view-atlas",
    SPINE_NAME: "hold_and_win_game_view",
    REEL_SET_KEY: HOLD_AND_WIN_GAME_VIEW_SLOTS.REEL_SET,
    OVERLAY_REEL_SET_KEY: HOLD_AND_WIN_GAME_VIEW_SLOTS.OVERLAY_REEL_SET,
    IDLE_ANIMATIONS: ["hnw_bg_idle", "idle"],
  };

  /**
   * Fit this room with ITS OWN padding (HOLD_AND_WIN_LAYOUT_CONFIGS) instead
   * of the base board's scale, and place it with its own offset table.
   */
  applyResponsiveScaling() {
    if (!this.dimensions || !this.spine) return;
    const { width, height } = this.dimensions;
    const scale = holdAndWinSceneScale(this.dimensions, this.layout);
    const offset = HOLD_AND_WIN_OFFSET_CONFIG[this.layout] ?? { x: 0, y: 0 };
    const unit = Settings.SYMBOL_HEIGHT * scale;
    this.spine.scale.set(scale);
    this.spine.position.set(
      width / 2 + unit * (offset.x ?? 0),
      // Y is SUBTRACTED so a positive value pushes the room up.
      height / 2 - unit * (offset.y ?? 0),
    );
  }

  /**
   * The reel box's own scale (HOLD_AND_WIN_REEL_SCALE_CONFIG), in place of the
   * base game's table. On BOTH bones, each times its own setup scale:
   *   `reels`           the box itself — used as is in portrait;
   *   `reel_landscape`  the landscape skin's constraint COPIES this bone's
   *                     transform onto `reels`, so a scale on `reels` alone
   *                     would be overwritten there.
   * Re-applied on every resize, after the layout skin (see resize).
   */
  applyReelBlockScale() {
    const skeleton = this.spine?.skeleton;
    if (!skeleton) return;
    const factor = HOLD_AND_WIN_REEL_SCALE_CONFIG[this.layout] ?? 1;

    // Landscape's box: the constraint target.
    const landscapeBox = skeleton.findBone("reel_landscape");
    if (landscapeBox) {
      landscapeBox.scaleX = landscapeBox.data.scaleX * factor;
      landscapeBox.scaleY = landscapeBox.data.scaleY * factor;
    }

    // The portrait group (identity in landscape — no entry): each bone's
    // setup position scaled about the root plus the offset, its setup scale
    // times the group's. The box also takes its own factor.
    const group = HOLD_AND_WIN_PORTRAIT_GROUP_CONFIG[this.layout];
    const groupScale = group?.scale ?? 1;
    const offsetX = (group?.x ?? 0) * Settings.SYMBOL_HEIGHT;
    const offsetY = (group?.y ?? 0) * Settings.SYMBOL_HEIGHT;
    for (const name of PORTRAIT_GROUP_BONES) {
      const bone = skeleton.findBone(name);
      if (!bone) continue;
      const k = groupScale * (name === "reels" ? factor : 1);
      bone.x = bone.data.x * groupScale + offsetX;
      bone.y = bone.data.y * groupScale + offsetY;
      bone.scaleX = bone.data.scaleX * k;
      bone.scaleY = bone.data.scaleY * k;
    }
  }

  /** The two board slots, plus the respin counter's. */
  attachSlotContainers() {
    super.attachSlotContainers();
    if (!this.spine?.skeleton) return;
    // ── The fire trails fly ABOVE the whole room ──
    // A sibling of the spine, drawn after it — over every cell, the counter
    // and the collector (Vladimir, 2026-09-29: "fire must be ABOVE the
    // collector, mandatory"). Chest collections and the End Collector both
    // fly here.
    if (!this.trailLayer) {
      this.trailLayer = new PIXI.Container();
      this.trailLayer.name = "hold_and_win_trail_layer";
      this.trailLayer.sortableChildren = true;
      this.trailLayer.zIndex = 50;
      this.container?.addChild(this.trailLayer);
    }
    this.respinCounterSlotContainer = this._attachContainer({
      name: "hold_and_win_respin_counter_slot_content",
      registryKey: HOLD_AND_WIN_GAME_VIEW_SLOTS.RESPIN_COUNTER,
      // The new export's `attachment_spin_counter`; our old hand-added name
      // as the fallback.
      slotNames: ["attachment_spin_counter", "respin_counter_placeholder"],
    });
  }

  /** The lock plates go in with the rest of the room. */
  initializeSpine() {
    super.initializeSpine();
    this.attachLockedRows();
  }

  /**
   * One `locked_reel` spine per lock slot, hidden until setUnlockedRows. In a
   * holder, like the reel anticipation: spine-pixi rewrites the slot object's
   * own visible every frame, so the spine inside is shown / hidden.
   */
  attachLockedRows() {
    if (this._lockedRows || !this.spine?.skeleton) return;
    const {
      SLOT_PREFIX,
      FIRST_LOCKED_ROW,
      COUNT,
      SPINE,
      TEXT_SLOT,
      COUNT_FONT_SIZE,
      MORE_FONT_SIZE,
      MORE_KEY,
    } = HOLD_AND_WIN_LOCKED_ROWS;
    const tag = this.constructor.SCENE.TAG;
    const assets = PIXI.Assets.cache;
    if (!assets.has(SPINE.SKELETON) || !assets.has(SPINE.ATLAS)) {
      console.warn(`[${tag}] Locked row spine not in cache`);
      return;
    }
    this._lockedRows = [];
    for (let i = 1; i <= COUNT; i++) {
      const slotName = `${SLOT_PREFIX}${i}`;
      if (!this.spine.skeleton.findSlot(slotName)) continue;
      const dataRow = FIRST_LOCKED_ROW + i - 1;
      try {
        const spine = Spine.from({ skeleton: SPINE.SKELETON, atlas: SPINE.ATLAS });
        spine.name = `locked_reel_${i}`;
        spine.visible = false;
        const holder = new PIXI.Container();
        holder.name = `locked_reel_${i}_slot_content`;
        holder.addChild(spine);
        this.spine.addSlotObject(slotName, holder);

        // "N MORE" — see setZoneCount. A slot object, so it fades with the
        // plate's `out` (spine-pixi copies the slot's alpha). Never
        // pixel-rounded: the spine scales it.
        const threshold = ZONE_THRESHOLDS.find(([, rows]) => rows === dataRow + 1)?.[0];
        const record = { dataRow, spine, threshold, count: null, more: null };
        if (threshold != null && spine.skeleton.findSlot(TEXT_SLOT)) {
          const label = new PIXI.Container();
          label.name = `locked_reel_${i}_text`;
          const makeText = (text, size) => {
            const t = new PIXI.Text(
              text,
              new PIXI.TextStyle(scaleStyle(FONT_STYLES.jackpots, size)),
            );
            t.anchor.set(0, 0.5);
            t.resolution = 2;
            t.roundPixels = false;
            label.addChild(t);
            return t;
          };
          record.count = makeText(String(threshold), COUNT_FONT_SIZE);
          record.more = makeText(i18next.t(MORE_KEY).toUpperCase(), MORE_FONT_SIZE);
          spine.addSlotObject(TEXT_SLOT, label);
          this._layoutLockedLabel(record);
        }
        this._lockedRows.push(record);
      } catch (error) {
        console.error(`[${tag}] Failed to create locked row spine:`, error);
      }
    }
    // Rows / count set before the plates existed.
    if (this._unlockedRows != null) this.setUnlockedRows(this._unlockedRows);
    if (this._cellsInZone != null) this.setZoneCount(this._cellsInZone);
  }

  /** "N MORE", right-aligned at RIGHT_EDGE. */
  _layoutLockedLabel({ count, more }) {
    if (!count || !more) return;
    const { GAP, RIGHT_EDGE } = HOLD_AND_WIN_LOCKED_ROWS;
    const start = RIGHT_EDGE - (count.width + GAP + more.width);
    count.x = start;
    more.x = start + count.width + GAP;
  }

  /**
   * The active zone's occupied cells (the server's cells_in_zone, or the
   * count as the landings play): every plate shows how many more open its
   * row. Never below 0 — at 0 the row is opening.
   */
  setZoneCount(cellsInZone) {
    this._cellsInZone = cellsInZone;
    (this._lockedRows || []).forEach((record) => {
      if (!record.count || record.threshold == null) return;
      const label = String(Math.max(0, record.threshold - cellsInZone));
      if (record.count.text === label) return;
      record.count.text = label;
      this._layoutLockedLabel(record);
    });
  }

  /**
   * The rows past `unlockedRows` (data rows) under their plate, at once — the
   * room's first frame, a reload. A plate already locked keeps its loop.
   */
  setUnlockedRows(unlockedRows) {
    this._unlockedRows = unlockedRows;
    const { IDLE, OUT } = HOLD_AND_WIN_LOCKED_ROWS;
    (this._lockedRows || []).forEach(({ dataRow, spine }) => {
      const state = spine.state;
      if (dataRow >= unlockedRows) {
        const current = state.getCurrent(0)?.animation?.name;
        if (spine.visible && current === IDLE) return;
        state.clearTracks();
        spine.skeleton.setToSetupPose();
        state.setAnimation(0, IDLE, true);
        spine.visible = true;
      } else {
        if (state.getCurrent(0)?.animation?.name === OUT) return;
        state.clearTracks();
        spine.skeleton.setToSetupPose();
        spine.visible = false;
      }
    });
  }

  /**
   * The rows from `from` to `to` (data rows) open: their plates play `out`,
   * then hide. Resolves when every one is done.
   */
  unlockRows(from, to) {
    this._unlockedRows = to;
    const { OUT } = HOLD_AND_WIN_LOCKED_ROWS;
    const opening = (this._lockedRows || []).filter(
      ({ dataRow, spine }) => dataRow >= from && dataRow < to && spine.visible,
    );
    return Promise.all(
      opening.map(
        ({ spine }) =>
          new Promise((resolve) => {
            const state = spine.state;
            const entry = state.setAnimation(0, OUT, false);
            if (!entry) {
              spine.visible = false;
              resolve();
              return;
            }
            entry.listener = {
              complete: () => {
                // A setUnlockedRows (a new room) may have taken the plate over.
                if (state.getCurrent(0) === entry) {
                  state.clearTracks();
                  spine.skeleton.setToSetupPose();
                  spine.visible = false;
                }
                resolve();
              },
              interrupt: () => resolve(),
            };
          }),
      ),
    );
  }

  /**
   * The coin counter — `count/40` in the Royal plate's box. Built on first
   * use (Pot Collection's pattern), a slot object so the plate's bones place
   * and scale it in both layouts. Never pixel-rounded: the spine scales it.
   */
  setCoinCounter(count, total = HOLD_AND_WIN_COIN_COUNTER.TOTAL) {
    const { SLOT, FONT_SIZE, MAX_WIDTH } = HOLD_AND_WIN_COIN_COUNTER;
    if (!this.spine?.skeleton?.findSlot(SLOT)) return;

    if (!this._coinCounterText || this._coinCounterText.destroyed) {
      const container = new PIXI.Container();
      container.name = "hold_and_win_coin_counter";
      const text = new PIXI.Text(
        "",
        // new PIXI.TextStyle(scaleStyle(FONT_STYLES.winText, FONT_SIZE)),
        // The plates' golden look (fontStyles `jackpots`, 2026-09-30).
        new PIXI.TextStyle(scaleStyle(FONT_STYLES.jackpots, FONT_SIZE)),
      );
      text.anchor.set(0.5);
      text.resolution = 2;
      text.roundPixels = false;
      container.addChild(text);
      this.spine.addSlotObject(SLOT, container);
      this._coinCounterText = text;
    }

    const text = this._coinCounterText;
    const label = `${count}/${total}`;
    if (text.text !== label) text.text = label;
    text.scale.set(1);
    if (text.width > MAX_WIDTH) text.scale.set(MAX_WIDTH / text.width);
  }

  destroy() {
    this._unregisterSlot?.(HOLD_AND_WIN_GAME_VIEW_SLOTS.RESPIN_COUNTER);
    this._coinCounterText = null;
    (this._lockedRows || []).forEach(({ spine }) => {
      if (!spine.destroyed) spine.destroy();
    });
    this._lockedRows = null;
    super.destroy();
  }
}

export default HoldAndWinGameViewController;
